import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ArrowRight, Layers } from "lucide-react";
import PublicPageLayout from "@/components/PublicPageLayout";
import Seo, { pageSchema } from "@/components/Seo";
import { WhatsAppButton } from "@/components/WhatsAppFab";
import { Button } from "@/components/ui/button";
import { WA_MESSAGES, COVERAGE_FACTS } from "@/lib/site";

export default function McaGstDatabase() {
  const path = "/mca-gst-database";
  const title = "MCA + GST Database | Combined Company Database India | Registry Feed";
  const description = "Get both MCA company data and GST business data in a single subscription. Daily-updated combined database from Registry Feed — one login, one dashboard, two data streams. Contact on WhatsApp for pricing.";
  const bc = [{ name: "Home", path: "/" }, { name: "MCA + GST Database", path }];
  const jsonLd = pageSchema({ title, description, path, breadcrumbs: bc });
  return (
    <PublicPageLayout breadcrumbs={bc}>
      <Seo title={title} description={description} path={path} jsonLd={jsonLd} />

      <section className="mx-auto max-w-[1400px] px-4 sm:px-6 py-14 sm:py-20">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground">
            <Layers className="h-3 w-3 text-primary" /> Combined Package
          </div>
          <h1 className="mt-6 font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight">
            MCA + GST Database — <span className="text-primary">two streams</span>, one subscription.
          </h1>
          <p className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Get both the MCA company database and the GST business database in a single Registry Feed account. Two daily uploads land in your dashboard — separate files, unified access.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <WhatsAppButton message={WA_MESSAGES.general} label="Get MCA + GST Combo" />
            <Link to="/sample-data"><Button size="lg" variant="outline">Request Sample</Button></Link>
            <Link to="/pricing"><Button size="lg" variant="ghost">Get Pricing <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
          </div>
        </div>
      </section>

      <section className="border-t border-border bg-muted/30">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 py-14">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight">Why the combined package?</h2>
          <ul className="mt-6 grid sm:grid-cols-2 gap-3 text-sm">
            {[
              "Cover both incorporation (MCA) and taxpayer-registration (GST) events",
              "One login, one dashboard, separate daily files per data type",
              "Pan-India or state-wise coverage",
              "30 days of daily deliveries per subscription",
            ].map((c) => (
              <li key={c} className="flex items-start gap-2 rounded-md border border-border bg-card px-3 py-2.5">
                <CheckCircle2 className="h-4 w-4 text-primary mt-0.5" /> {c}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="border-t border-border">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 py-14">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight">Coverage &amp; delivery</h2>
          <ul className="mt-6 grid sm:grid-cols-2 gap-2 text-sm">
            {COVERAGE_FACTS.map((c) => (
              <li key={c} className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-primary mt-0.5" /> {c}</li>
            ))}
          </ul>
          <div className="mt-8 flex flex-wrap gap-3">
            <WhatsAppButton message={WA_MESSAGES.general} label="Buy MCA + GST Combo" />
            <Link to="/pricing"><Button variant="outline">See Pricing</Button></Link>
            <Link to="/contact"><Button variant="ghost">Talk to Sales</Button></Link>
          </div>
        </div>
      </section>
    </PublicPageLayout>
  );
}
